<script setup>
import { router } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'

const props = defineProps({
  element:       { type: Object,  default: () => ({}) },
  estimation:    { type: Object,  default: () => ({}) },
  partie_suivie: { type: Object,  default: () => ({}) },
  back_url:      { type: String,  default: '#' },
  can_see_costs: { type: Boolean, default: false },
  can_update:    { type: Boolean, default: false },
})

function fmtCurrency(val) {
  if (val === null || val === undefined || val === '') return '—'
  return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2 }).format(val)
}

function fmtQty(val) {
  if (val === null || val === undefined || val === '') return '—'
  return new Intl.NumberFormat('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val)
}

function toggleActif() {
  const nom = props.element.code
  const msg = props.element.actif
    ? (props.element.dependant
        ? `« ${nom} » est utilisé dans des rapports et sera désactivé. Continuer ?`
        : `Supprimer définitivement « ${nom} » ?`)
    : `Réactiver « ${nom} » ?`
  if (!confirm(msg)) return
  router.delete(props.element.destroy_url)
}
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="flex items-center gap-3">
        <a :href="back_url" class="text-outline hover:text-on-surface transition-colors">
          <span class="material-symbols-outlined text-xl">arrow_back</span>
        </a>
        <span class="text-outline">/</span>
        <span class="text-sm font-black uppercase tracking-widest text-on-surface">
          {{ partie_suivie.code }} &rsaquo; {{ element.code }}
        </span>
      </div>
    </template>

    <template #topbar-right>
      <div v-if="can_update" class="flex items-center gap-2">
        <a :href="element.edit_url"
           class="px-4 py-2.5 text-xs font-black uppercase tracking-widest rounded-md text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 transition-all flex items-center gap-1.5">
          <span class="material-symbols-outlined text-sm">edit</span>Modifier
        </a>
        <button
          @click="toggleActif"
          class="px-4 py-2.5 text-xs font-black uppercase tracking-widest rounded-md text-outline hover:text-error bg-surface-container-low border border-outline-variant/10 transition-all flex items-center gap-1.5"
        >
          <span class="material-symbols-outlined text-sm">{{ element.actif ? 'toggle_off' : 'toggle_on' }}</span>
          {{ element.actif ? (element.dependant ? 'Désactiver' : 'Effacer') : 'Activer' }}
        </button>
      </div>
    </template>

    <div class="p-8 space-y-6">

      <!-- Header -->
      <div>
        <h1 class="text-3xl font-black text-on-surface tracking-tight uppercase">
          {{ partie_suivie.code }} — {{ element.code }}
        </h1>
        <p class="text-outline text-sm font-black uppercase tracking-widest mt-1">
          Estimation <a :href="estimation.show_url" class="text-primary hover:brightness-125 transition-all">{{ estimation.code }}</a>
        </p>
      </div>

      <!-- Détails -->
      <div class="bg-surface-container rounded-xl overflow-hidden shadow-xl border border-outline-variant/10 max-w-3xl">
        <div class="px-6 py-4 bg-surface-container-high/60 border-b border-outline-variant/15 flex items-center justify-between">
          <h2 class="text-[11px] font-black uppercase tracking-widest text-on-surface">Détails</h2>
          <div v-if="element.actif" class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-tertiary/10 border border-tertiary/20">
            <span class="w-1.5 h-1.5 rounded-full bg-tertiary animate-pulse"></span>
            <span class="text-[9px] font-black uppercase tracking-widest text-tertiary">Actif</span>
          </div>
          <div v-else class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-surface-container-highest border border-outline-variant/20">
            <span class="w-1.5 h-1.5 rounded-full bg-outline/50"></span>
            <span class="text-[9px] font-black uppercase tracking-widest text-outline">Inactif</span>
          </div>
        </div>
        <table class="w-full text-left border-collapse">
          <tbody class="divide-y divide-outline-variant/10">
            <tr class="hover:bg-surface-container-highest/20 transition-colors">
              <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-48">Code</th>
              <td class="px-6 py-3 font-mono text-sm font-bold text-primary">{{ element.code }}</td>
            </tr>
            <tr class="hover:bg-surface-container-highest/20 transition-colors">
              <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-48">Description</th>
              <td class="px-6 py-3 text-sm text-on-surface">{{ element.description || '—' }}</td>
            </tr>
            <tr class="hover:bg-surface-container-highest/20 transition-colors">
              <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-48">Quantité</th>
              <td class="px-6 py-3 font-mono text-sm text-on-surface">
                {{ fmtQty(element.quantite) }}<span v-if="element.unite" class="text-outline ml-1">{{ element.unite }}</span>
              </td>
            </tr>
            <tr v-if="element.unite" class="hover:bg-surface-container-highest/20 transition-colors">
              <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-48">Unité</th>
              <td class="px-6 py-3 text-sm text-on-surface">{{ element.unite }}</td>
            </tr>
            <tr v-if="can_see_costs" class="hover:bg-surface-container-highest/20 transition-colors">
              <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-48">Taux unitaire</th>
              <td class="px-6 py-3 font-mono text-sm font-bold text-on-surface">
                {{ fmtCurrency(element.taux_unitaire) }}<span v-if="element.unite" class="text-outline ml-1">/ {{ element.unite }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Footer -->
      <footer class="mt-12 pb-6 text-center text-outline text-[10px] uppercase tracking-widest font-black">
        Copyright &copy; 2016-{{ new Date().getFullYear() }} aurapport.com inc.
      </footer>

    </div>
  </AppLayout>
</template>
